import { lastCompleteDayUtc, MV_STALE } from "@/lib/mv-stale-time";

export type DatePreset = "current-month" | "previous-month" | "ytd" | "last-12-months" | "custom";

export type NamedPreset = Exclude<DatePreset, "custom">;

/** Tokeny presetów dopuszczone w URL (`?preset=`), w kolejności paska. */
export const NAMED_PRESETS = ["current-month", "previous-month", "ytd", "last-12-months"] as const;

export const PRESET_LABELS: Record<DatePreset, string> = {
	"current-month": "Bieżący miesiąc (do D-1)",
	"previous-month": "Poprzedni miesiąc",
	ytd: "Od początku roku (do D-1)",
	"last-12-months": "12 pełnych miesięcy",
	custom: "Własny zakres",
};

/**
 * Krótkie podpisy na przyciskach paska. Pełne etykiety (z „(do D-1)”)
 * nie mieściły się w jednym wierszu filtra na 1920 px i pasek dostawał
 * poziomy scroll; pełna wersja zostaje w tooltipie i w linii „Dane za…”.
 */
export const PRESET_SHORT_LABELS: Record<NamedPreset, string> = {
	"current-month": "Bieżący miesiąc",
	"previous-month": "Poprzedni miesiąc",
	ytd: "Od początku roku",
	"last-12-months": "12 pełnych mies.",
};

/**
 * Zakres jest „pełny”, gdy zaczyna się 1. dnia miesiąca i kończy ostatnim
 * dniem miesiąca — wtedy żaden miesiąc w środku nie jest ucięty. Bieżący
 * miesiąc do D-1 i YTD do D-1 są z definicji niepełne; to jest sygnał dla
 * użytkownika, że suma nie jest porównywalna z pełnym miesiącem.
 */
export function isClosedMonthRange(dateFrom: string, dateTo: string): boolean {
	if (dateFrom.slice(8, 10) !== "01") return false;
	const y = Number(dateTo.slice(0, 4));
	const m = Number(dateTo.slice(5, 7));
	return dateTo === formatLocalDate(new Date(y, m, 0));
}

/**
 * Okno zakresu dashboardu — lustro backendowego `DASHBOARD_MV_MAX_AGE_DAYS`
 * (430 dni w definicji MV minus 30 dni zapasu na staleness refreshu).
 */
export const DASHBOARD_RANGE_MAX_AGE_DAYS = 400;

/**
 * Rodziny MV wyznaczające wspólny zakres dat dashboardu. Max daty w pickerze =
 * najmniejszy z ich ostatnich KOMPLETNYCH dni (lustro backendowego `dataAsOf`).
 *
 * Zakupy czyta wyłącznie koszyk hurtowy. Backend przycina jego zakres do
 * rzeczywistej historii sukcesów (`clamp_range_to_data_as_of`); planowany
 * koniec porannego cyklu nie zastępuje tego potwierdzenia.
 */
const DASHBOARD_MV_SPECS = [MV_STALE.dashboardSalesDaily, MV_STALE.dailySales] as const;

function formatLocalDate(d: Date): string {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

function formatUtcDate(d: Date): string {
	const y = d.getUTCFullYear();
	const m = String(d.getUTCMonth() + 1).padStart(2, "0");
	const day = String(d.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
}

export function firstOfMonthIso(d = new Date()): string {
	return formatLocalDate(new Date(d.getFullYear(), d.getMonth(), 1));
}

export function todayIso(d = new Date()): string {
	return formatLocalDate(d);
}

export function isoDaysAgo(days: number, d = new Date()): string {
	const t = new Date(d);
	t.setDate(t.getDate() - days);
	return formatLocalDate(t);
}

/**
 * Górna granica wyboru dat: ostatni kompletny dzień wspólny dla wszystkich
 * rodzin MV dashboardu wg ich harmonogramów cron (UTC). Backend waliduje
 * autorytatywnie (422 z minDate/maxDate) — to tylko lustrzana granica UI.
 */
export function dashboardMaxDateIso(now = new Date()): string {
	const days = DASHBOARD_MV_SPECS.map((spec) => lastCompleteDayUtc(spec, now.getTime()).getTime());
	return formatUtcDate(new Date(Math.min(...days)));
}

/** Dolna granica wyboru dat — okno dziennych MV dashboardu. */
export function dashboardMinDateIso(now = new Date()): string {
	const t = new Date(now);
	t.setDate(t.getDate() - DASHBOARD_RANGE_MAX_AGE_DAYS);
	return formatLocalDate(t);
}

/**
 * Bieżący miesiąc ma sens dopiero, gdy zawiera choć jeden kompletny dzień —
 * 1. dnia miesiąca (i 2. przed porannym refreshem) `maxDate` wskazuje jeszcze
 * poprzedni miesiąc i preset jest niedostępny.
 */
export function currentMonthAvailable(now: Date, maxDateIso: string): boolean {
	return firstOfMonthIso(now) <= maxDateIso;
}

/**
 * Domyślny zakres: od pierwszego dnia miesiąca do ostatniego kompletnego
 * dnia. `maxDateIso` — autorytatywny max z `meta.dataAsOf` (harmonogram
 * lokalny jest tylko bootstrap fallbackiem przed pierwszą odpowiedzią).
 * Gdy bieżący miesiąc nie ma ani jednego kompletnego dnia, domyślny jest
 * pełny poprzedni miesiąc — jednodniowy zakres 1. dnia miesiąca mylił.
 */
export function defaultDashboardRange(
	now = new Date(),
	maxDateIso?: string,
): { dateFrom: string; dateTo: string } {
	const dateTo = maxDateIso ?? dashboardMaxDateIso(now);
	if (!currentMonthAvailable(now, dateTo)) return previousMonthRange(now, dateTo);
	return { dateFrom: firstOfMonthIso(now), dateTo };
}

/**
 * Pełny poprzedni miesiąc kalendarzowy. `new Date(y, m - 1, 1)` i
 * `new Date(y, m, 0)` normalizują przełom roku same (grudzień poprzedniego
 * roku), więc styczeń nie wymaga osobnej gałęzi. Koniec i tak przycinamy do
 * `maxDate` — 1-2 dnia miesiąca ostatni kompletny dzień bywa jeszcze w
 * poprzednim miesiącu, a zakres poza `dataAsOf` to 422 z backendu.
 */
function previousMonthRange(now: Date, maxDateIso: string) {
	const first = formatLocalDate(new Date(now.getFullYear(), now.getMonth() - 1, 1));
	const last = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), 0));
	const dateTo = last <= maxDateIso ? last : maxDateIso;
	return { dateFrom: first <= dateTo ? first : dateTo, dateTo };
}

/**
 * Od 1 stycznia do ostatniego kompletnego dnia. Rok bierzemy z `dateTo`, nie
 * z zegara: 1-2 stycznia `dataAsOf` wskazuje jeszcze grudzień i YTD liczony
 * po zegarze zwinąłby się do jednego dnia. Dolne odcięcie to okno 400 dni —
 * starszej daty backend nie przyjmie.
 */
function ytdRange(now: Date, maxDateIso: string) {
	const jan1 = `${maxDateIso.slice(0, 4)}-01-01`;
	const minIso = dashboardMinDateIso(now);
	const dateFrom = jan1 >= minIso ? jan1 : minIso;
	return { dateFrom: dateFrom <= maxDateIso ? dateFrom : maxDateIso, dateTo: maxDateIso };
}

/**
 * 12 pełnych ZAMKNIĘTYCH miesięcy: ostatni miesiąc zakresu to ostatni
 * miesiąc, który `maxDate` pokrywa w całości. Zwykle to miesiąc poprzedni;
 * tylko gdy `maxDate` jest ostatnim dniem miesiąca, ten miesiąc też wchodzi.
 * Bieżący, niepełny miesiąc NIGDY nie wchodzi — na serii pełnych miesięcy
 * wyglądałby jak spadek. Dolne odcięcie to okno 400 dni (13+ miesięcy, więc
 * w praktyce nie tnie).
 */
function closedMonthsRange(months: number, now: Date, maxDateIso: string) {
	const y = Number(maxDateIso.slice(0, 4));
	const m = Number(maxDateIso.slice(5, 7)) - 1;
	const maxIsMonthEnd = maxDateIso === formatLocalDate(new Date(y, m + 1, 0));
	const lastMonth = maxIsMonthEnd ? m : m - 1;
	const dateTo = formatLocalDate(new Date(y, lastMonth + 1, 0));
	const first = formatLocalDate(new Date(y, lastMonth - (months - 1), 1));
	const minIso = dashboardMinDateIso(now);
	const dateFrom = first >= minIso ? first : minIso;
	return { dateFrom: dateFrom <= dateTo ? dateFrom : dateTo, dateTo };
}

const DETECTABLE_PRESETS = NAMED_PRESETS;

export function detectPreset(
	dateFrom: string,
	dateTo: string,
	now = new Date(),
	maxDateIso?: string,
): DatePreset {
	const max = maxDateIso ?? dashboardMaxDateIso(now);
	// 1. dnia miesiąca `current-month` zwraca zakres poprzedniego miesiąca
	// (fallback wyżej) — bez odsiania podświetlałby się zamiast previous-month.
	const detectable = currentMonthAvailable(now, max)
		? DETECTABLE_PRESETS
		: DETECTABLE_PRESETS.filter((p) => p !== "current-month");
	// Porównanie z tym, co dany preset REALNIE by ustawił — `previous-month`
	// kończy się przed `max`, więc wcześniejszy skrót „dateTo !== max = custom"
	// nigdy by go nie podświetlił.
	const hit = detectable.find((preset) => {
		const range = applyPreset(preset, now, max);
		return range.dateFrom === dateFrom && range.dateTo === dateTo;
	});
	return hit ?? "custom";
}

/**
 * Który preset ma być podświetlony. W styczniu `current-month` i `ytd` dają
 * IDENTYCZNY zakres (1 stycznia → maxDate), więc sam zakres nie wystarcza do
 * rozstrzygnięcia. Reguła:
 *  1. jawny wybór użytkownika (`picked`) wygrywa, dopóki zakres nadal równa
 *     się temu, co ten preset by ustawił — klik w YTD zostaje na YTD;
 *  2. bez jawnego wyboru (wejście z URL, F5, zakres odtworzony ze stanu)
 *     decyduje kolejność `DETECTABLE_PRESETS`, czyli `current-month` przed
 *     `ytd`. Deterministyczne, niezależne od historii sesji.
 */
export function resolveActivePreset(
	dateFrom: string,
	dateTo: string,
	picked: NamedPreset | null,
	now = new Date(),
	maxDateIso?: string,
): DatePreset {
	const max = maxDateIso ?? dashboardMaxDateIso(now);
	// `?preset=current-month` sprzed doby otwarty 1. dnia miesiąca — preset
	// niedostępny, rozstrzyga detekcja (poprzedni miesiąc).
	if (picked === "current-month" && !currentMonthAvailable(now, max)) {
		return detectPreset(dateFrom, dateTo, now, max);
	}
	if (picked) {
		const range = applyPreset(picked, now, max);
		if (range.dateFrom === dateFrom && range.dateTo === dateTo) return picked;
	}
	return detectPreset(dateFrom, dateTo, now, max);
}

export function applyPreset(preset: NamedPreset, now = new Date(), maxDateIso?: string) {
	const dateTo = maxDateIso ?? dashboardMaxDateIso(now);
	switch (preset) {
		case "current-month":
			return defaultDashboardRange(now, dateTo);
		case "previous-month":
			return previousMonthRange(now, dateTo);
		case "ytd":
			return ytdRange(now, dateTo);
		case "last-12-months":
			return closedMonthsRange(12, now, dateTo);
	}
}
